// src/shared/order.js
//
// Turns a signed agreement into a structured ORDER — the machine-readable answer
// to "what did this customer actually buy?"
//
// Today the answer only exists as pixels in a PDF and a bag of signing "spots".
// Neither can be invoiced or provisioned against. This module derives, from the
// draft's OWN frozen sections + the spots captured at signing, a flat list of
// line items with amounts, plus reconciled totals.
//
// It is the payload the planned integrations consume:
//   • QuickBooks  — invoice lines (via qbItemId, once the SKU catalog lands)
//   • License site— entitlements to provision (via licenseCode)
//   • Signed PDF  — will render from this in a later phase
//
// Two rules this module must never break:
//  1. AMOUNTS COME FROM shared/pricing.js. No parallel math, ever — that split is
//     exactly what once let a declined core product keep billing.
//  2. Totals must reconcile with calcTotal/calcTotalYear to the cent. tools/test-order.js
//     asserts this across every real product; if it fails, the order is lying.
//
// Frozen at sign time: the order is computed from the sections snapshot the client
// actually saw, so later catalog/price edits can never restate a signed deal.
const { buildPricingState, rowLinePrice, parsePrice, calcTotal, calcTotalYear, bankRowId } = require('./pricing');
const { resolvePaymentPlan } = require('./payment');
const { billingFor, planEligible, CATALOG_VERSION } = require('./catalog');

const ORDER_VERSION = 1;

// A row is only a real line item if it represents something purchasable.
// Filters out layout rows that live in pricing tables: "— Or —" dividers,
// section headers, and total rows.
function isLineItemRow(row) {
  if (!row || row.isTotal) return false;
  if (!row.id) return false;
  if (row.pricing || row.qty || row.initial) return true;
  // Any authored price makes it a line — including non-numeric ones like
  // "Included" or "N/A". Those are real entitlements the customer receives
  // (bundled support hours, etc.) and the license site must still provision
  // them; they carry amount 0 and never move totals. Rows with no price at all
  // are layout ("— Or —" dividers, group headers) and are not line items.
  const p = (row.price || '').trim();
  return p !== '' && p !== '$';
}

// Mirrors calcTotal's gate EXACTLY: an initial row that is not a pricing row
// only counts once the client has initialed it. Keep in lockstep with pricing.js.
function isBillable(row, state) {
  if (row.initial && !row.pricing && !(state && state.selected)) return false;
  return true;
}

function lineFor(row, state, ctx) {
  const dual = ctx.dualYear;
  const computed = rowLinePrice(row, state);          // null when unpriced ("Included", "N/A", "")
  const year1 = dual ? parsePrice(row.price) : computed;
  const year2 = dual ? parsePrice(row.price2) : null;

  const qtyEntered = row.qty ? (state && state.qty != null ? Number(state.qty) : null) : null;
  const unlimited = !!(row.qty && state && state.unlimited);

  return {
    rowId: row.id,
    name: (row.module || '').trim(),
    product: ctx.product || null,
    table: ctx.table || null,
    kind: row.qty ? 'quantity' : 'flat',
    qty: unlimited ? 'unlimited' : (row.qty ? (qtyEntered != null ? Math.max(0, Math.floor(qtyEntered)) : 0) : 1),
    unitPrice: row.pricing && row.pricing.unit != null ? row.pricing.unit : null,
    amount: year1 == null ? 0 : year1,
    year2Amount: dual ? (year2 == null ? 0 : year2) : null,
    // An entitlement the customer gets at no charge ("Included", "N/A") — still
    // provisionable, so the license site needs to see it. Never affects totals.
    included: year1 == null,
    // Billing shape: the row's OWN value wins — rows authored through the
    // pricing-table editor capture it at creation, and it rides along in the
    // frozen snapshot so a later reclassification can never restate a signed
    // deal. Older hand-authored rows have none, so fall back to the catalog
    // (classified by rowId); anything neither knows stays 'unclassified'.
    billing: row.billing || billingFor(row.id),
    paymentPlanOk: row.paymentPlanOk != null ? !!row.paymentPlanOk : planEligible(row.id),
    // SKU codes are deliberately unassigned (team hasn't labeled them yet); the
    // QuickBooks / license-site identifiers hang off the SKU once it exists.
    sku: null,
    qbItemId: null,
    licenseCode: null,
  };
}

// draft: needs .sections (the frozen snapshot) and .spots (captured at signing)
function buildOrder(draft) {
  const sections = (draft && draft.sections) || [];
  const spots = (draft && draft.spots) || {};
  const state = buildPricingState(spots);

  // buildPricingState intentionally tracks only what affects price, so the
  // explicit "No thanks" marker is read straight off the spots here. The
  // distinction matters legally: a customer who actively declined an option is
  // not the same as one who simply never got to it.
  const declinedRows = new Set();
  for (const s of Object.values(spots)) {
    if (s && s.rowId && s.declined) declinedRows.add(s.rowId);
  }

  const lineItems = [];
  const declined = [];
  let grand = 0;
  let grandYear2 = 0;

  for (const section of sections) {
    if (section.type === 'pricing_table') {
      const rows = section.rows || [];
      const ctx = { product: section._module || null, table: section.label || null, dualYear: !!section.dualYear };
      for (const row of rows) {
        if (!isLineItemRow(row)) continue;
        const st = state[row.id];
        if (!isBillable(row, st)) {
          declined.push({
            rowId: row.id,
            name: (row.module || '').trim(),
            product: ctx.product,
            table: ctx.table,
            // explicit "No thanks" vs simply never initialed — both are non-purchases,
            // but an explicit decline is worth keeping for the audit trail.
            explicit: declinedRows.has(row.id),
          });
          continue;
        }
        const line = lineFor(row, st, ctx);
        lineItems.push(line);
        grand += line.amount;
        if (line.year2Amount) grandYear2 += line.year2Amount;
      }
    }

    if (section.type === 'bank_of_hours') {
      const rate = section.rate || 180;
      const id = bankRowId(section);
      const st = state[id];
      const hours = st && st.qty != null ? Math.max(0, Math.floor(Number(st.qty)) || 0) : 0;
      if (hours > 0) {
        const amount = hours * rate;
        lineItems.push({
          rowId: id,
          name: 'Prepaid Bank of Support Hours',
          product: section._module || null,
          table: null,
          kind: 'bank_hours',
          qty: hours,
          unitPrice: rate,
          amount,
          year2Amount: null,
          included: false,
          // Bank ids ('bank__<module>') aren't catalog rows; prepaid hours are
          // by nature a one-time purchase, not eligible for installments.
          billing: 'one_time',
          paymentPlanOk: false,
          sku: null,
          qbItemId: null,
          licenseCode: null,
        });
        grand += amount;
      }
    }
  }

  // Bucket by billing shape. Everything lands in `unclassified` until the billing
  // classification is applied to the catalog; the shape is ready for it now.
  const byBilling = {};
  for (const li of lineItems) byBilling[li.billing] = (byBilling[li.billing] || 0) + li.amount;

  return {
    version: ORDER_VERSION,
    computedAt: new Date().toISOString(),
    currency: 'USD',
    lineItems,
    declined,
    totals: {
      grand,
      year2: grandYear2 || null,
      byBilling,
    },
    // Per-deal payment arrangement (upfront vs installments). Deliberately NOT a
    // catalog property — the same line is paid differently by different customers.
    // Percentages resolved to dollars against the signed total; the instalments
    // always sum to totals.grand exactly.
    paymentPlan: resolvePaymentPlan(draft && draft.paymentPlan, grand),
    catalogVersion: CATALOG_VERSION,
  };
}

// Reconciliation check: the order's totals must equal what the pricing engine
// reports for the same sections/spots. Used by tests and callable in anger.
function reconcile(draft) {
  const order = buildOrder(draft);
  const sections = (draft && draft.sections) || [];
  const state = buildPricingState((draft && draft.spots) || {});
  let engineTotal = 0;
  let engineYear2 = 0;
  for (const s of sections) {
    if (s.type !== 'pricing_table') continue;
    if (s.dualYear) {
      engineTotal += parsePrice(calcTotalYear(s.rows, state, 1)) || 0;
      engineYear2 += parsePrice(calcTotalYear(s.rows, state, 2)) || 0;
    } else {
      engineTotal += parsePrice(calcTotal(s.rows, state)) || 0;
    }
  }
  // bank-of-hours lines are not part of any pricing table's total
  const bankTotal = order.lineItems.filter(l => l.kind === 'bank_hours').reduce((a, l) => a + l.amount, 0);
  return {
    ok: (order.totals.grand - bankTotal) === engineTotal && (order.totals.year2 || 0) === engineYear2,
    orderTotal: order.totals.grand,
    orderTotalExBank: order.totals.grand - bankTotal,
    engineTotal,
    orderYear2: order.totals.year2 || 0,
    engineYear2,
  };
}

module.exports = { buildOrder, reconcile, ORDER_VERSION };
